'use client';

import FontFaceObserver from 'fontfaceobserver';
import type { GetCampaignResponse } from '@ab-inbev-eu/campaign/src/models/campaign';
import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { cn } from '@ab-inbev-eu/ui-library/components/utils';
import { useSelectedLayoutSegment } from 'next/navigation';

import { FullPageSpinner } from '../FullPageSpinner';
import { extractLanguageAtIso2 } from '../../utils/functions.shared';
import { publicConfig } from '../../utils/config';

// No need to put this in the campaign config as legal components are not customizable
const LEGAL_COMPONENT_FONTS: GetCampaignResponse['branding']['fonts'] = {
  legalHeader: {
    name: 'Barlow',
    url: 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Roboto:ital,wght@0,100;0,400;0,700;1,400&display=swap',
  },
  legalText: {
    name: 'Work Sans',
    url: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&family=Roboto:ital,wght@0,100;0,400;0,700;1,400&display=swap',
  },
};

export function ThemeProvider(
  props: Readonly<{ template: GetCampaignResponse['branding']; lng: string; children: React.ReactNode }>,
) {
  const segment = useSelectedLayoutSegment();
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const loaded = useMemo(() => fontsLoaded && imagesLoaded, [fontsLoaded, imagesLoaded]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imagesLoaded && wrapperRef.current && imageRef.current) {
      wrapperRef.current.style.backgroundImage = `url(${imageRef.current.src})`;
    }
  }, [imagesLoaded]);

  useEffect(() => {
    const root = document.querySelector<HTMLHtmlElement>(':root');

    if (!root) return;
    const fonts = new Map<string, { name: string; weight: string }>();
    Object.entries(props.template.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value as string);
      document.body.classList.add('bg-neutral-0');
    });

    let loadedFonts = 0;
    const usedUrls = new Set<string>();
    Object.entries({ ...props.template.fonts, ...LEGAL_COMPONENT_FONTS }).forEach(([key, value]) => {
      const { name, url, fontSize, fontWeight, lineHeight, textTransform } = value as Record<string, string>;
      const id = `link-font-${key}`;
      fonts.set(`${name}-${fontWeight}`, { name: name, weight: fontWeight });

      if (document.getElementById(id)) return;

      const onFontLoad = () => {
        root.style.setProperty(`--font-${key}`, name);
        if (fontSize) root.style.setProperty(`--font-size-${key}`, fontSize);
        if (lineHeight) root.style.setProperty(`--line-height-${key}`, lineHeight);
        if (fontWeight) root.style.setProperty(`--font-weight-${key}`, fontWeight);
        if (textTransform) root.style.setProperty(`--text-transform-${key}`, textTransform);
      };

      // In campaign template it is possible to face fonts duplication.
      // That's why we need to check if font is already loaded
      // to avoid unnecessary requests
      if (usedUrls.has(url)) {
        onFontLoad();
      } else {
        usedUrls.add(url);
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url.startsWith('/') ? `${publicConfig().NEXT_PUBLIC_BASE_PATH ?? ''}${url}` : url;
        link.id = id;
        document.head.appendChild(link);
        link.onload = onFontLoad;
      }
    });

    fonts.forEach((font) => {
      // we need to use FontFaceObserver to check if font is loaded
      const fontFace = new FontFaceObserver(font.name, { weight: font.weight });
      fontFace
        .load(null, 10000)
        .then(() => {
          loadedFonts++;
          if (loadedFonts === fonts.size) setFontsLoaded(true);
        })
        .catch((e: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load font:', font.name, e);
        });
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  const bgImage = (props.template.assets as any).backgroundImageUrl as Record<string, string>;

  return (
    <div ref={wrapperRef} className="relative min-h-dvh bg-cover bg-top bg-no-repeat">
      {!loaded && <FullPageSpinner />}
      <Image
        ref={imageRef}
        fill
        src={bgImage[extractLanguageAtIso2(props.lng)] || bgImage['en']}
        alt=""
        className={cn('invisible !static', {
          'max-h-dvh': !!segment, // background should be scrollable only on main page
        })}
        onLoad={() => {
          setImagesLoaded(true);
        }}
      />

      {loaded && props.children}
    </div>
  );
}
